import { mutate } from 'swr'
import { apiSubscribe } from '@/services/BillingService'
import { usePlanActivationStore } from '@/store/planActivationStore'
import { openPaddleCheckout, type CheckoutOutcome } from '@/utils/paddle'

export type ChoosePlanOutcome = 'subscribed' | CheckoutOutcome

/**
 * Refetch everything billing-shaped: the summary, the catalogue, the features
 * every gate reads, invoices. A plan change moves all of them, and they are
 * cached under separate keys (see `useBilling`).
 *
 * The account itself too (`['org']`): buying Team makes a personal account a
 * company one, and Settings → Organization should say so without a reload.
 */
export const revalidateBilling = () =>
    mutate(
        (key) =>
            Array.isArray(key) && (key[0] === 'billing' || key[0] === 'org'),
    )

/**
 * Choose a plan, whichever provider the deployment uses.
 *
 * `subscribed` — the server did it in one call (Stripe, any free plan, or a
 * Paddle subscriber changing plan). `completed` — Paddle's checkout took the
 * payment and the plan will follow by webhook. `closed` — the customer left
 * the checkout without paying, and nothing changed.
 *
 * Shared by the billing page, the drawer's plan dialog and onboarding so none
 * of them can drift on what a checkout response means. It also does the
 * follow-up itself — refetch now, or wait for the webhook — because onboarding
 * navigates away the moment this resolves, and the wait has to outlive it.
 */
export async function choosePlan(
    planCode: string,
    locale?: string,
): Promise<ChoosePlanOutcome> {
    const result = await apiSubscribe(planCode)
    if ('checkout' in result) {
        const outcome = await openPaddleCheckout(result.checkout, { locale })
        if (outcome === 'completed') {
            usePlanActivationStore.getState().start(planCode)
        }
        return outcome
    }
    void revalidateBilling()
    return 'subscribed'
}
